import type { ReactiveController, ReactiveControllerHost } from 'lit';

/**
 * Houdt de message-list "stuck to bottom" wanneer er nieuwe berichten
 * binnenkomen, behalve wanneer de bezoeker bewust naar boven heeft
 * gescrold om eerdere berichten te lezen.
 *
 * Vijf samenwerkende mechanismen:
 *   1. Lit lifecycle (`willUpdate` → `measure`, `updated` → `applyIfStuck`):
 *      vangt synchroon-zichtbare DOM-veranderingen op. Meet vóór render,
 *      scroll na render.
 *   2. Capture-phase `load`-listener op de scroll-container: vangt
 *      `<img>`-load events op die niet bubbelen (data-URI uploads,
 *      YouTube-thumbnails). Een image-decode-finish ná de Lit-update
 *      verandert `scrollHeight`; zonder dit zou een net-verstuurd foto-
 *      bericht onderaan blijven hangen.
 *   3. MutationObserver op de scroll-container (subtree childList):
 *      vangt async DOM-mutaties binnen child-components op. Concreet:
 *      `embrace-message-list` vervangt nieuwe bot-replies eerst 800ms
 *      door een typing-indicator en swap't 'm dan via z'n eigen
 *      `requestUpdate()` om voor de echte bubble — die swap verandert
 *      scrollHeight zonder dat de parent's Lit-lifecycle fired.
 *   4. ResizeObserver op de scroll-container: vangt resize-events van
 *      de container zelf op (panel grow/shrink, viewport-change).
 *   5. Scroll-listener op dezelfde container: herijkt `stickToBottom`
 *      bij elke (ook handmatige) scroll. Zonder dit zouden de andere
 *      callbacks een bezoeker die tijdens image-loading omhoog-scrollt
 *      alsnog naar bottom katapulteren.
 *
 * De drempel (`stickThresholdPx`) bepaalt hoe ver van bottom nog telt als
 * "leest het laatste bericht". 100px geeft de bezoeker speling om een
 * regel hoger te scrollen zonder het auto-scroll-gedrag te verliezen.
 */
export class AutoScrollController implements ReactiveController {
  private readonly getScrollContainer: () => HTMLElement | null;
  private readonly stickThresholdPx: number;
  private stickToBottom = true;
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private observedEl: HTMLElement | null = null;

  constructor(
    host: ReactiveControllerHost,
    getScrollContainer: () => HTMLElement | null,
    options: { stickThresholdPx?: number } = {},
  ) {
    this.getScrollContainer = getScrollContainer;
    this.stickThresholdPx = options.stickThresholdPx ?? 100;
    host.addController(this);
  }

  hostConnected(): void {
    /* lazy-attach in applyIfStuck — scroll-container bestaat nog niet */
  }

  hostDisconnected(): void {
    this.detachObservers();
  }

  /** Aanroepen vanuit host's `willUpdate()`. */
  measure(): void {
    const el = this.getScrollContainer();
    if (!el) return;
    this.stickToBottom = this.isNearBottom(el);
  }

  /** Aanroepen vanuit host's `updated()`. */
  applyIfStuck(): void {
    this.ensureObservers();
    if (!this.stickToBottom) return;
    this.scrollToBottomNextFrame();
  }

  private ensureObservers(): void {
    const el = this.getScrollContainer();
    if (!el || el === this.observedEl) return;
    this.detachObservers();
    this.observedEl = el;
    this.resizeObserver = new ResizeObserver(() => this.rescrollIfStuck());
    this.resizeObserver.observe(el);
    this.mutationObserver = new MutationObserver(() => this.rescrollIfStuck());
    this.mutationObserver.observe(el, { childList: true, subtree: true });
    el.addEventListener('scroll', this.handleScroll, { passive: true });
    // Capture-phase: `load` op <img> bubbelt niet, maar capture-listeners
    // vangen ze toch op. Zo zien we elke image-decode-finish — exact het
    // moment waarop scrollHeight async groeit.
    el.addEventListener('load', this.handleLoad, { capture: true, passive: true });
  }

  private detachObservers(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    if (this.observedEl) {
      this.observedEl.removeEventListener('scroll', this.handleScroll);
      this.observedEl.removeEventListener('load', this.handleLoad, { capture: true });
      this.observedEl = null;
    }
  }

  private readonly handleScroll = (): void => {
    if (this.observedEl) this.stickToBottom = this.isNearBottom(this.observedEl);
  };

  private readonly handleLoad = (e: Event): void => {
    const target = e.target as HTMLElement | null;
    if (target?.tagName !== 'IMG') return;
    this.rescrollIfStuck();
  };

  private rescrollIfStuck(): void {
    if (this.stickToBottom && this.observedEl) {
      this.observedEl.scrollTop = this.observedEl.scrollHeight;
    }
  }

  private isNearBottom(el: HTMLElement): boolean {
    return el.scrollHeight - el.scrollTop - el.clientHeight < this.stickThresholdPx;
  }

  private scrollToBottomNextFrame(): void {
    requestAnimationFrame(() => {
      const el = this.observedEl ?? this.getScrollContainer();
      if (el) el.scrollTop = el.scrollHeight;
    });
  }
}
